/**
 * ユーザーアイコン（アバター）API
 *
 * 画像そのものはS3に保存され、ここで扱うのはURLだけ。
 * 解除するとカスタムアイコンのURLが消えるだけなので、Google連携ユーザーは
 * Googleのアイコン（Cognitoのpicture属性）に自動で戻る。
 */
import type { components } from '@/types/api-schema';
import { throwIfRateLimited } from '@/services/apiErrors';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

type UserAvatarResponse = components['schemas']['UserAvatarResponse'];

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  const body = await response.json().catch(() => null);
  if (body && typeof body === 'object' && 'message' in body) {
    const { message } = body as { message?: unknown };
    if (typeof message === 'string' && message) return message;
  }
  return fallback;
}

/**
 * アイコンを設定する
 * @param token Cognito JWT トークン
 * @param imageDataUri トリミング済み画像の data URI
 * @returns 設定後のアイコンURL
 */
export async function uploadAvatar(token: string, imageDataUri: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/user/avatar`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ image: imageDataUri }),
  });

  if (!response.ok) {
    throwIfRateLimited(response);
    throw new Error(await readErrorMessage(response, 'アイコンの保存に失敗しました'));
  }

  const data: UserAvatarResponse = await response.json();
  if (!data.avatarUrl) {
    throw new Error('アイコンの保存に失敗しました');
  }
  return data.avatarUrl;
}

/**
 * カスタムアイコンを解除する（Google連携ならGoogleのアイコンに戻る）
 * @param token Cognito JWT トークン
 */
export async function deleteAvatar(token: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/user/avatar`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throwIfRateLimited(response);
    throw new Error(await readErrorMessage(response, 'アイコンの解除に失敗しました'));
  }
}
